/**
 * Search Management - Moon Moon Agency
 * Handles search overlay functionality
 */

/*********************************************************************************************
 * 🔍 SEARCH MODULE
 */
const SearchManager = {
	// Local configuration
	config: {
		selectors: {
			searchContainer: '[data-search-container]',
			searchInput: '[data-search-input]',
			searchInputHeader: '[data-search-input-header]',
			searchToggle: '[data-search-toggle]',
			predictiveSearch: '[data-predictive-search]',
			predictiveSearchDefault: '#predictive-search-default'
		},
		classes: {
			stopScrolling: 'stop-scrolling',
			open: 'mm-open'
		},
		timing: {
			searchFocusDelay: 100,
			searchBlurDelay: 100
		}
	},

	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for search overlay
	 */
	setupFocusTrap(searchContainer, triggerElement) {
		const focusableElements = searchContainer.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		this.focusTrap.handleKeyDown = (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault();
				this.hide();
				this.focusTrap.triggerElement?.focus();
				return;
			}

			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
		}
	},

	/**
	 * Show search overlay
	 */
	show(triggerElement) {
		let searchContainer = null;
		const allContainers = document.querySelectorAll(this.config.selectors.searchContainer);

		if (window.innerWidth <= 768) {
			for (const container of allContainers) {
				if (container.closest('.mm-mobile') || !container.closest('.mm-desktop')) {
					searchContainer = container;
					break;
				}
			}
		}

		if (!searchContainer && allContainers.length > 0) {
			searchContainer = allContainers[0];
		}

		if (!searchContainer) return;

		const searchInput = searchContainer.querySelector(this.config.selectors.searchInput);
		const predictiveSearchDefault = searchContainer.querySelector(this.config.selectors.predictiveSearchDefault);

		document.documentElement.classList.add(this.config.classes.stopScrolling);
		searchContainer.classList.add(this.config.classes.open);
		searchContainer.setAttribute('aria-hidden', 'false');
		searchContainer.removeAttribute('inert');
		triggerElement?.setAttribute('aria-expanded', 'true');

		if (!searchContainer._mmBackdropHandler) {
			searchContainer._mmBackdropHandler = () => this.hide();
			searchContainer.addEventListener('click', searchContainer._mmBackdropHandler);
		}

		const inner = searchContainer.querySelector('[data-search-modal-inner]');
		if (inner && !inner._mmInnerClickHandler) {
			inner._mmInnerClickHandler = (e) => e.stopPropagation();
			inner.addEventListener('click', inner._mmInnerClickHandler);
		}

		if (predictiveSearchDefault) {
			const inputValue = searchInput ? searchInput.value.trim() : '';
			if (inputValue === '') {
				predictiveSearchDefault.style.opacity = '0';
				predictiveSearchDefault.classList.remove('mm-none');
				predictiveSearchDefault.style.display = 'flex';
				predictiveSearchDefault.style.visibility = 'visible';
				requestAnimationFrame(() => {
					predictiveSearchDefault.style.opacity = '1';
				});
			} else {
				predictiveSearchDefault.classList.add('mm-none');
				predictiveSearchDefault.style.display = 'none';
			}
		}

		setTimeout(() => {
			const overlayInput = searchInput || document.getElementById('Search');
			if (overlayInput) overlayInput.focus();
			this.setupFocusTrap(searchContainer, triggerElement);
		}, this.config.timing.searchFocusDelay);

		document.dispatchEvent(new CustomEvent('global:search-opened'));
	},

	/**
	 * Hide search overlay
	 */
	hide() {
		const allContainers = document.querySelectorAll(this.config.selectors.searchContainer);

		allContainers.forEach((searchContainer) => {
			if (!searchContainer.classList.contains(this.config.classes.open)) return;

			document.documentElement.classList.remove(this.config.classes.stopScrolling);
			searchContainer.classList.remove(this.config.classes.open);
			searchContainer.setAttribute('aria-hidden', 'true');
			searchContainer.setAttribute('inert', '');
			window.getElements(this.config.selectors.searchToggle).forEach(b => b.setAttribute('aria-expanded', 'false'));

			if (searchContainer._mmBackdropHandler) {
				searchContainer.removeEventListener('click', searchContainer._mmBackdropHandler);
				delete searchContainer._mmBackdropHandler;
			}

			const inner = searchContainer.querySelector('[data-search-modal-inner]');
			if (inner?._mmInnerClickHandler) {
				inner.removeEventListener('click', inner._mmInnerClickHandler);
				delete inner._mmInnerClickHandler;
			}

			const searchInput = searchContainer.querySelector(this.config.selectors.searchInput);
			if (searchInput) searchInput.value = '';

			const mmPredSearch = searchContainer.querySelector('mm-predictive-search');
			if (mmPredSearch?._abort) {
				mmPredSearch._abort.abort();
				mmPredSearch._abort = null;
			}

			setTimeout(() => {
				const predictiveSearchDefault = searchContainer.querySelector(this.config.selectors.predictiveSearchDefault);
				if (predictiveSearchDefault) {
					predictiveSearchDefault.classList.add('mm-none');
					predictiveSearchDefault.style.display = 'none';
				}
				const results = searchContainer.querySelector('#predictive-search');
				const empty = searchContainer.querySelector('#predictive-search-empty');
				if (results) results.style.display = 'none';
				if (empty) empty.style.display = 'none';
			}, 350);
		});

		this.removeFocusTrap();
		document.dispatchEvent(new CustomEvent('global:search-closed'));
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.SearchManager = SearchManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmShowSearch = (triggerElement) => SearchManager.show(triggerElement);
window.mmHideSearch = () => SearchManager.hide();

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	// Search close button
	const searchCloseBtn = document.querySelector('[data-search-close]');
	if (searchCloseBtn) {
		searchCloseBtn.addEventListener('click', () => SearchManager.hide());
	}

	// Search input sync + default content toggle
	const searchInput = document.getElementById('Search');
	const predictiveSearchDefault = document.querySelector('#predictive-search-default');

	const syncAndToggleDefault = (value) => {
		if (predictiveSearchDefault) {
			if (value.trim() === '') {
				const results = document.querySelector('#predictive-search');
				const empty = document.querySelector('#predictive-search-empty');
				if (results) results.style.display = 'none';
				if (empty) empty.style.display = 'none';

				predictiveSearchDefault.style.opacity = '0';
				predictiveSearchDefault.classList.remove('mm-none');
				predictiveSearchDefault.style.display = 'flex';
				predictiveSearchDefault.style.visibility = 'visible';
				requestAnimationFrame(() => {
					predictiveSearchDefault.style.opacity = '1';
				});
			} else {
				predictiveSearchDefault.classList.add('mm-none');
				predictiveSearchDefault.style.display = 'none';
			}
		}
	};

	if (searchInput) {
		searchInput.addEventListener('input', (e) => syncAndToggleDefault(e.target.value));
	}
});
